import React from 'react';
import {
  Activity,
  Clock,
  ArrowRight,
  Users,
  Heart
} from 'lucide-react';

const Services: React.FC = () => {
  const services = [
    {
      icon: Activity,
      title: "Reabilitação Músculo-Esquelética",
      description: "Tratamento com foco na recuperação de lesões musculares, articulares e ósseas.",
      conditions: ["Lesões musculares", "Entorses", "Fraturas", "Pós-cirúrgico"],
      duration: "50 min",
      featured: false
    },
    {
      icon: Activity,
      title: "Tendinopatias",
      description: "Abordagem específica para tendinopatias, epicondilites e síndromes relacionadas.",
      conditions: ["Epicondilite", "Tendinite", "Bursite", "Síndrome do impacto"],
      duration: "50 min",
      featured: false
    },
    {
      icon: Activity,
      title: "Dores na Coluna",
      description: "Tratamento para dores cervicais, dorsais e lombares com exercícios específicos.",
      conditions: ["Lombalgia", "Cervicalgia", "Hérnia de disco", "Escoliose"],
      duration: "50 min",
      featured: false
    },
    {
      icon: Activity,
      title: "Fascite Plantar",
      description: "Tratamento com foco na dor no calcanhar e região plantar do pé com técnicas específicas.",
      conditions: ["Fascite plantar", "Esporão de calcâneo", "Dor no pé", "Metatarsalgia"],
      duration: "50 min",
      featured: false
    },
    {
      icon: Activity,
      title: "DTM (Disfunção Temporomandibular)",
      description: "Fisioterapia focada em problemas da articulação temporomandibular.",
      conditions: ["Dor na ATM", "Limitação de abertura", "Estalos"],
      duration: "50 min",
      featured: false
    },
    {
      icon: Activity,
      title: "Dores em Ombros e Joelhos",
      description: "Tratamento para dores articulares, limitações de movimento, entre outros.",
      conditions: ["Capsulite Adesiva", "Lesão de menisco", "Artrose", "Condromalácia"],
      duration: "50 min",
      featured: false
    },
    {
      icon: Activity,
      title: "Pilates (Solo)",
      description: "Exercícios de Pilates adaptados para reabilitação e fortalecimento muscular.",
      conditions: ["Fortalecimento core", "Postura corporal", "Flexibilidade", "Equilíbrio"],
      duration: "50 min",
      featured: false
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Conforto Domiciliar",
      description: "Tratamento no ambiente familiar, sem deslocamentos"
    },
    {
      icon: Users,
      title: "Atendimento Personalizado",
      description: "Cada sessão é adaptada às suas necessidades específicas"
    }
  ];

  const scrollToContactForm = () => {
    const element = document.getElementById('formulario-contato');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="servicos" className="py-16 px-6 sm:px-8 sm:py-20 lg:py-24 bg-blue-100">
      <div className="container-custom">
        {/* Header da seção */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 sm:mb-8">
            Serviços Oferecidos
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Tratamentos personalizados de fisioterapia e pilates (Solo), 
            focados na sua recuperação e bem-estar.
          </p>
        </div>

        {/* Grid de serviços */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 lg:mb-20">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={index}
                className="p-5 sm:p-6 rounded-xl shadow-md hover:shadow-lg hover:scale-105 hover:ring-2 hover:ring-primary-500 transition-all duration-300 bg-white"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
                </div>
                
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                
                <p className="text-xs sm:text-sm text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="space-y-3 mb-5">
                  <div className="flex items-center text-xs sm:text-sm text-primary-600">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    <span>Duração aprox.: {service.duration}</span>
                  </div>
                  
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Condições tratadas:</p>
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                      {service.conditions.join(", ")}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={scrollToContactForm}
                  className="w-full mt-auto flex items-center justify-center space-x-2 py-3 px-4 border border-primary-500 text-primary-500 rounded-lg hover:bg-primary-500 hover:text-white transition-colors duration-300 text-sm font-medium min-h-[44px]"
                >
                  <span>Entrar em Contato</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Benefícios do tratamento */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10 mb-12 sm:mb-16">
          <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-bold text-gray-900 text-center mb-8 sm:mb-10">
            Por que escolher o tratamento domiciliar?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <IconComponent className="w-8 h-8 sm:w-10 sm:h-10 text-primary-500" />
                  </div>
                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3">{benefit.title}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 sm:p-10 lg:p-12 text-white">
          <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-bold mb-6">
            Pronto para iniciar seu tratamento?
          </h3>
          <p className="text-sm sm:text-base text-primary-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Não deixe a dor limitar sua qualidade de vida. Entre em contato e agende sua avaliação.
          </p>
          <button
            onClick={scrollToContactForm}
            className="bg-white text-primary-500 font-semibold py-4 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-300 inline-flex items-center space-x-2 text-sm sm:text-base min-h-[48px]"
          >
            <span>Entrar em Contato</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;